import React from "react";
import { Download, Image as ImageIcon, Video, Music2 } from "lucide-react";

/**
 * /downloads — a dead-simple page for Robert to grab every image and video
 * we've made. Uses the download attribute so iOS Safari triggers the proper
 * "Save to Files / Photos" share sheet instead of an inline preview.
 */
const BASE = "/api";

const DESIGNS = [
  { file: "album-cover-everythang-redneck.png", name: "REDNECK REVOLUTION — Album Cover" },
  { file: "rednecks-bikers-brothers-never-hide-v1.png", name: "Rednecks · Bikers · Brothers (Never Hide) — T-shirt" },
  { file: "beer-thirty-somewhere-v1.png", name: "Beer:30 Somewhere — T-shirt" },
  { file: "tailgate-therapy-v1.png", name: "Tailgate Therapy — T-shirt" },
  { file: "mud-over-pavement-v1.png", name: "Mud > Pavement — T-shirt" },
  { file: "whiskey-bent-hell-bound-v1.png", name: "Whiskey Bent & Hell Bound — T-shirt" },
  { file: "we-have-heart-fixed-v1.png", name: "We Have Heart (Reissue) — T-shirt" },
  { file: "hand-in-hand-redneck-rich-v1.png", name: "Hand In Hand · Redneck Rich — T-shirt" },
];

const VIDEO_CLIPS = [
  { file: "together-we-rise-short.mp4", name: "Together We Rise — 22s clip" },
  { file: "redneck-rich-short.mp4", name: "Redneck Rich — 22s clip" },
  { file: "crimson-conquerors-short.mp4", name: "Crimson Conquerors — 22s clip" },
  { file: "redneck-rebirth-short.mp4", name: "Redneck Rebirth — 22s clip" },
  { file: "united-we-ride-short.mp4", name: "United We Ride — 22s clip" },
  { file: "crimson-brotherhood-short.mp4", name: "Crimson Brotherhood — 22s clip" },
  { file: "redneck-revolution-short.mp4", name: "Redneck Revolution — 22s clip" },
  { file: "warriors-of-the-sunrise-short.mp4", name: "Warriors of the Sunrise — 22s clip" },
];

function VideoPreviewRow({ file, name }) {
  const previewSrc = `${BASE}/videos/${file}`; // inline preview
  const downloadSrc = `${BASE}/videos/${file}?download=1`; // save-to-camera-roll
  return (
    <div
      className="bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors p-4"
      data-testid={`video-row-${file}`}
    >
      <div className="grid md:grid-cols-[240px_1fr] gap-4 items-center">
        <div className="bg-[#0A0E0B] border-2 border-[#DD5422] overflow-hidden aspect-[9/16] max-w-[240px] mx-auto md:mx-0">
          <video
            src={previewSrc}
            controls
            playsInline
            preload="metadata"
            data-testid={`video-preview-${file}`}
            className="w-full h-full object-cover"
          >
            Sorry, your browser doesn&apos;t support video preview.
          </video>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-anton text-lg uppercase tracking-tight leading-tight">{name}</div>
            <div className="text-xs text-[#8D9688] uppercase tracking-widest mt-0.5">MP4 · vertical 9:16 · tap play to preview</div>
          </div>
          <a
            href={downloadSrc}
            download
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`video-download-${file}`}
            className="self-start inline-flex items-center gap-2 px-4 py-3 bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-xs hover:bg-[#FF6B35] border-2 border-[#DD5422]"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} />
            Save to Camera Roll
          </a>
        </div>
      </div>
    </div>
  );
}

function DownloadRow({ href, name, kind, thumb }) {
  const Icon = kind === "video" ? Video : ImageIcon;
  return (
    <div
      className="flex items-center gap-4 bg-[#151A14] border-2 border-[#2C332A] p-4 hover:border-[#DD5422] transition-colors"
      data-testid={`download-row-${name}`}
    >
      {thumb ? (
        <img src={thumb} alt="" className="w-16 h-16 object-cover border-2 border-[#DD5422] shrink-0" />
      ) : (
        <div className="w-16 h-16 bg-[#0A0E0B] border-2 border-[#DD5422] flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-[#DD5422]" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-anton text-lg uppercase tracking-tight leading-tight truncate">{name}</div>
        <div className="text-xs text-[#8D9688] uppercase tracking-widest mt-0.5">{kind === "video" ? "MP4 · vertical 9:16" : "PNG · high-res"}</div>
      </div>
      <a
        href={href}
        download
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`download-btn-${name}`}
        className="shrink-0 flex items-center gap-2 px-4 py-3 bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-xs hover:bg-[#FF6B35] border-2 border-[#DD5422]"
      >
        <Download className="w-4 h-4" strokeWidth={2.5} />
        Download
      </a>
    </div>
  );
}

export default function Downloads() {
  return (
    <div className="min-h-screen max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="downloads-page">
      <div className="mb-10">
        <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-2">/ Owner Downloads</div>
        <h1 className="font-anton text-4xl md:text-6xl uppercase tracking-tight leading-none">Grab Everything.</h1>
        <p className="text-[#8D9688] mt-4 max-w-2xl">
          Every design and video clip we&apos;ve built, in one place. Tap Download → iOS asks whether to save to Files or Photos. Upload to Printful, TikTok, Facebook, wherever.
        </p>
      </div>

      <section className="mb-12">
        <div className="flex items-center gap-3 mb-4">
          <ImageIcon className="w-6 h-6 text-[#DD5422]" />
          <h2 className="font-anton text-2xl md:text-3xl uppercase tracking-tight">T-Shirt Designs</h2>
        </div>
        <div className="space-y-3">
          {DESIGNS.map((d) => (
            <DownloadRow
              key={d.file}
              href={`${BASE}/designs/${d.file}?download=1`}
              thumb={`${BASE}/designs/${d.file}`}
              name={d.name}
              kind="image"
            />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-4">
          <Video className="w-6 h-6 text-[#DD5422]" />
          <h2 className="font-anton text-2xl md:text-3xl uppercase tracking-tight">Social Video Clips</h2>
        </div>
        <p className="text-[#8D9688] text-sm mb-4 max-w-2xl uppercase tracking-widest">
          Tap the play button on any clip to preview it. When you&apos;re happy, tap &quot;Save to Camera Roll&quot; and upload to TikTok / Reels / Facebook.
        </p>
        <div className="space-y-4">
          {VIDEO_CLIPS.map((v) => (
            <VideoPreviewRow key={v.file} file={v.file} name={v.name} />
          ))}
        </div>
      </section>

      <div className="mt-12 bg-[#151A14] border-2 border-[#DD5422] p-6">
        <div className="font-anton uppercase tracking-widest text-sm text-[#DD5422] mb-2 flex items-center gap-2">
          <Music2 className="w-4 h-4" /> How to save on iPhone
        </div>
        <ol className="text-[#8D9688] text-sm space-y-2 list-decimal ml-5">
          <li>Tap the orange <strong className="text-[#E5E5E5]">Download</strong> button next to any file.</li>
          <li>iOS will show a share sheet with options like <em>Save to Files</em>, <em>Save Image</em>, <em>Save Video</em>.</li>
          <li>Tap <strong className="text-[#E5E5E5]">Save Image</strong> or <strong className="text-[#E5E5E5]">Save Video</strong> to send it straight to your camera roll.</li>
          <li>If those aren&apos;t there, tap <strong className="text-[#E5E5E5]">Save to Files</strong> → open the Files app → find it → then Share → Save Image / Save Video.</li>
        </ol>
      </div>
    </div>
  );
}
